import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const QUESTS = [
  { id: 'hero-top', label: 'Quest I: Risveglio al Checkpoint', hash: '#hero-top' },
  { id: 'iniziativa', label: 'Quest II: Tira per l\'Iniziativa', hash: '#iniziativa' },
  { id: 'diagnosi', label: 'Quest III: Analisi dei Debuff', hash: '#diagnosi' },
  { id: 'differenza', label: 'Quest IV: La Differenza di Metodo', hash: '#differenza' },
  { id: 'archetipo', label: 'Quest V: Identifica la tua Classe', hash: '#archetipo' },
  { id: 'roadmap', label: 'Quest VI: La Progressione di Livello', hash: '#roadmap' },
  { id: 'lead-game', label: 'Quest VII: Affronta il Libro-Game', hash: '#lead-game' },
]

export default function FloatingHUDQuestTracker() {
  const [activeQuestIdx, setActiveQuestIdx] = useState(0)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showHUD, setShowHUD] = useState(false)

  useEffect(() => {
    // Mostriamo l'HUD dopo 1 secondo dall'ingresso per non intasare l'inquadratura iniziale
    const t = setTimeout(() => setShowHUD(true), 1200)

    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight / 3

      // Trova quale sezione è attualmente sotto l'indicatore di scorrimento
      for (let i = QUESTS.length - 1; i >= 0; i--) {
        const el = document.getElementById(QUESTS[i].id)
        if (el) {
          const rect = el.getBoundingClientRect()
          const absoluteTop = window.scrollY + rect.top
          if (scrollPos >= absoluteTop) {
            setActiveQuestIdx(i)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    // Inizializzazione al mount
    handleScroll()

    return () => {
      clearTimeout(t)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollToSection = (hash) => {
    const el = document.getElementById(hash.replace('#', ''))
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <AnimatePresence>
      {showHUD && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ type: 'spring', damping: 20 }}
          className="fixed right-4 top-1/3 z-50 hidden lg:flex flex-col items-end"
        >
          {/* Pulsante di collasso */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-7 h-7 rounded-lg border border-nerd-purple/35 bg-[#050510]/85 backdrop-blur-md text-nerd-cyan hover:text-nerd-white flex items-center justify-center mb-1.5 transition-all text-xs outline-none"
            style={{ boxShadow: '0 0 10px rgba(139,92,246,0.15)' }}
            title={isCollapsed ? "Espandi Registro Missioni" : "Riduci Registro"}
          >
            {isCollapsed ? '📜' : '✕'}
          </button>

          {/* Pannello Quest Log */}
          <motion.div
            animate={{ width: isCollapsed ? '0px' : '230px', opacity: isCollapsed ? 0 : 1 }}
            transition={{ type: 'spring', damping: 22 }}
            className="overflow-hidden rounded-xl border border-nerd-purple/30 bg-[#050510]/80 backdrop-blur-lg p-4 text-left relative"
            style={{
              boxShadow: '0 8px 32px rgba(5,5,16,0.65), 0 0 20px rgba(139,92,246,0.06)',
              display: isCollapsed ? 'none' : 'block'
            }}
          >
            {/* Corner decals */}
            <div className="absolute top-1 left-1 w-2 h-2 border-l border-t border-nerd-cyan/30" />
            <div className="absolute top-1 right-1 w-2 h-2 border-r border-t border-nerd-cyan/30" />
            <div className="absolute bottom-1 left-1 w-2 h-2 border-l border-b border-nerd-cyan/30" />
            <div className="absolute bottom-1 right-1 w-2 h-2 border-r border-b border-nerd-cyan/30" />

            <div className="flex items-center gap-2 mb-3 border-b border-nerd-purple/15 pb-1.5">
              <span className="text-[10px] text-nerd-purple font-display uppercase tracking-widest font-bold">Registro Missioni</span>
            </div>

            <div className="space-y-3">
              {QUESTS.map((quest, idx) => {
                const isActive = idx === activeQuestIdx
                const isCompleted = idx < activeQuestIdx
                const isUpcoming = idx > activeQuestIdx

                return (
                  <div
                    key={quest.id}
                    onClick={() => scrollToSection(quest.hash)}
                    className="cursor-pointer group flex items-start gap-2.5 select-none"
                  >
                    {/* Indicatore Stato Quest */}
                    <div className="shrink-0 mt-1">
                      {isCompleted ? (
                        <div className="w-3.5 h-3.5 rounded bg-nerd-cyan/15 border border-nerd-cyan flex items-center justify-center text-nerd-cyan text-[9px] font-bold">
                          ✓
                        </div>
                      ) : isActive ? (
                        <motion.div 
                          className="w-3.5 h-3.5 rounded border flex items-center justify-center text-[7px]"
                          style={{ borderColor: '#8b5cf6', background: 'rgba(139,92,246,0.1)' }}
                          animate={{ scale: [1, 1.15, 1], boxShadow: ['0 0 0px rgba(139,92,246,0)', '0 0 8px rgba(139,92,246,0.5)', '0 0 0px rgba(139,92,246,0)'] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-nerd-purple" />
                        </motion.div>
                      ) : (
                        <div className="w-3.5 h-3.5 rounded border border-nerd-gray/25 flex items-center justify-center" />
                      )}
                    </div>

                    {/* Testo Quest */}
                    <div className="flex-1">
                      <span
                        className="text-[10px] font-display font-semibold transition-all duration-300 block leading-tight"
                        style={{
                          color: isActive 
                            ? '#f5f5f5' 
                            : isCompleted 
                              ? '#9ca3af' 
                              : 'rgba(156,163,175,0.4)',
                        }}
                      >
                        {quest.label}
                      </span>
                      {isActive && (
                        <span className="text-[7px] text-nerd-purple uppercase tracking-widest font-semibold font-display mt-0.5 block animate-pulse">
                          In corso...
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-4 pt-2 border-t border-nerd-purple/15 flex items-center justify-between text-[7px] text-nerd-gray/60 font-display uppercase tracking-wider">
              <span>Progresso: {Math.round(((activeQuestIdx) / (QUESTS.length - 1)) * 100)}%</span>
              <span className="text-nerd-cyan">Livello {activeQuestIdx + 1}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
